import { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, Package, Trash2, Link2 } from 'lucide-react';
import { useOnboardingStore } from '../store/onboarding-store';
import { FunnelData, Product } from '../types/index';
import { getProductDetail } from '../../../services/store/productAxios';
import { dictionary, Language } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { getListOfFunnelsForStoreWizard } from '../../../services/generativeAI/generativeAIAxios/generateCopysAxios';
import { decodeTokenService } from '../../../services/decodeTokenService';
import type { Website } from '../../../interfaces/websites';
export interface ProductContext {
  product_name: string;
  product_description: string;
}

export interface PayloadBrandContext {
  selected_sale_angle: string;
  products: ProductContext[];
  language: Language;
}

interface StepSelectFunnelsProps {
  setPayloadBrandContext: (payloadBrandContext: PayloadBrandContext[]) => void;
  payloadBrandContext: PayloadBrandContext[];
}

type WebsiteSummary = {
  id: string;
  brand_name?: string;
  name?: string;
  product?: {
    id?: string;
    name?: string;
    description?: string;
    price?: string | number;
    image?: string;
  };
  products?: Array<{ product_id: string; type: string }>;
  sale_angle?: { name?: string; description?: string };
  url?: string;
  created_at?: string;
  updated_at?: string;
  configurations?: Website["configurations"];
  id_sequence_owner_id?: number;
  type?: Website["type"];
};

const PRODUCT_PLACEHOLDER_IMAGE = "/placeholder.svg?height=100&width=100";

const summaryProductToProduct = (product?: WebsiteSummary["product"], fallbackId?: string): Product => ({
  id: product?.id || fallbackId,
  name: product?.name || "Sin nombre",
  price: product?.price?.toString() || "0",
  image: product?.image || PRODUCT_PLACEHOLDER_IMAGE,
});

const detailToProduct = (detailProduct: any, fallbackId: string, fallback?: Product): Product => {
  const images = Array.isArray(detailProduct?.images) ? detailProduct.images : [];
  return {
    id: detailProduct?.id || fallback?.id || fallbackId,
    name: detailProduct?.name || fallback?.name || "Sin nombre",
    price: detailProduct?.price?.toString() || fallback?.price || "0",
    image: detailProduct?.base_image_url || detailProduct?.best_image_url || images[0] || fallback?.image || PRODUCT_PLACEHOLDER_IMAGE,
  };
};

const mapSummaryToWebsite = (site: WebsiteSummary, userId: string): Website => ({
  userId,
  projectId: "",
  websiteId: site.id.toString(),
  name: site.brand_name || site.name || site.product?.name || "",
  productName: site.product?.name || site.name || site.brand_name || "",
  type: site.type || "FUNNEL",
  status: "Online",
  url: site.url || "",
  sales_angle: site.sale_angle?.name || "",
  sales_angle_description: site.sale_angle?.description || site.product?.description || "",
  visits: 0,
  products: site.products || (site.product?.id ? [{ product_id: site.product.id, type: "PRINCIPAL" }] : []),
  productImage: site.product?.image || "",
  conversionRate: 0,
  theme: "Default Theme",
  lastEdited: new Date(site.updated_at || site.created_at || Date.now()).toISOString(),
  createdAt: new Date(site.created_at || Date.now()).toISOString(),
  configurations: site.configurations || { enabled: false, name_funnel: "" },
  idSequence: site.id_sequence_owner_id || 0,
  brand_name: site.brand_name || site.name || "",
});

const mapSummaryToPreviewFunnel = (site: WebsiteSummary): FunnelData => ({
  id: site.id.toString(),
  number: (site.id_sequence_owner_id || 0).toString(),
  name: site.brand_name || site.name || site.product?.name || "",
  mainProduct: summaryProductToProduct(site.product),
  totalProducts: site.products?.length || (site.product?.id ? 1 : 0),
});

export function StepSelectFunnels({ setPayloadBrandContext, payloadBrandContext }: StepSelectFunnelsProps) {
  const { language } = useAuth();
  const { data, addFunnel, removeFunnel, setProductLandingLink } = useOnboardingStore();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [websiteSummaries, setWebsiteSummaries] = useState<WebsiteSummary[]>([]);
  const [funnelForOnboarding, setFunnelForOnboarding] = useState<FunnelData[]>([]);
  const { selectedFunnels } = data;
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [websitesLoading, setWebsitesLoading] = useState(true);
  const [websitesLoadError, setWebsitesLoadError] = useState<string | null>(null);
  const [funnelBrandContextMap, setFunnelBrandContextMap] = useState<Map<string, PayloadBrandContext>>(new Map())

  useEffect(() => {
    let cancelled = false;

    const loadStoreWizardWebsites = async () => {
      setWebsitesLoading(true);
      setWebsitesLoadError(null);

      try {
        const userId = decodeTokenService.getUserIdFromToken() || "";
        const websitesData: WebsiteSummary[] = await getListOfFunnelsForStoreWizard();
        if (cancelled) return;

        const validSummaries = websitesData.filter((site) => (site.products?.length || 0) > 0);
        setWebsiteSummaries(validSummaries);
        setWebsites(validSummaries.map((site) => mapSummaryToWebsite(site, userId)));
        setFunnelForOnboarding(validSummaries.map(mapSummaryToPreviewFunnel));
      } catch (error) {
        console.error("Error cargando embudos para tiendas:", error);
        if (cancelled) return;
        setWebsiteSummaries([]);
        setWebsites([]);
        setFunnelForOnboarding([]);
        setWebsitesLoadError("No pudimos cargar tus embudos. Revisa la conexión e intenta de nuevo.");
      } finally {
        if (!cancelled) {
          setWebsitesLoading(false);
        }
      }
    };

    loadStoreWizardWebsites();

    return () => {
      cancelled = true;
    };
  }, []);

  const buildSelectedFunnel = async (website: Website, summary?: WebsiteSummary) => {
    let mainProduct: Product = summaryProductToProduct(summary?.product);
    let orderBump: Product | undefined;
    let upsell: Product | undefined;
    const productsContext: ProductContext[] = [];

    const resolvedProducts = await Promise.all(
      website.products.map(async (product) => {
        const fallback = product.type === "PRINCIPAL" ? mainProduct : undefined;

        try {
          const detailProduct: any = await getProductDetail(product.product_id);
          const productData = detailToProduct(detailProduct, product.product_id, fallback);

          productsContext.push({
            product_name: productData.name,
            product_description: detailProduct?.description || summary?.product?.description || "Sin descripción",
          });

          return { type: product.type, data: productData };
        } catch (error) {
          console.error(`Error obteniendo producto ${product.product_id}:`, error);
          const productData = fallback || {
            id: product.product_id,
            name: "Error",
            price: "0",
            image: PRODUCT_PLACEHOLDER_IMAGE,
          };

          productsContext.push({
            product_name: productData.name,
            product_description: summary?.product?.description || "No se pudo cargar la descripción",
          });

          return { type: product.type, data: productData };
        }
      })
    );

    resolvedProducts.forEach(({ type, data }) => {
      if (type === "PRINCIPAL") {
        mainProduct = data;
      } else if (type === "UP_SELL") {
        upsell = data;
      } else if (type === "BUMP_OFFER") {
        orderBump = data;
      }
    });

    const funnelResponse: FunnelData = {
      id: website.websiteId,
      number: website.idSequence.toString(),
      name: website.name,
      mainProduct,
      totalProducts: website.products.length,
    };

    if (orderBump?.name) {
      funnelResponse.orderBump = orderBump;
    }
    if (upsell?.name) {
      funnelResponse.upsell = upsell;
    }

    const brandContextResponse: PayloadBrandContext = {
      selected_sale_angle: website.sales_angle_description,
      products: productsContext,
      language: language as Language,
    };

    return { funnelResponse, brandContextResponse };
  };

  useEffect(() => {
    const updatePayloadBrandContext = () => {
      const selectedBrandContexts: PayloadBrandContext[] = []

      selectedFunnels.forEach((selectedFunnel) => {
        const brandContext = funnelBrandContextMap.get(selectedFunnel.id)
        if (brandContext) {
          selectedBrandContexts.push(brandContext)
        }
      })

      setPayloadBrandContext(selectedBrandContexts)
    }

    updatePayloadBrandContext()
  }, [selectedFunnels, funnelBrandContextMap])

  const filteredFunnels = funnelForOnboarding.filter(funnel =>
    funnel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    funnel.mainProduct?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableFunnels = filteredFunnels.filter(funnel =>
    !selectedFunnels.some(selected => selected.id === funnel.id)
  );
  const selectorLoading = websitesLoading || loading;

  const selectedProductLandingOptions = useMemo(() => {
    const selectedProductIds = new Set<string>();
    selectedFunnels.forEach((funnel) => {
      [funnel.mainProduct, funnel.orderBump, funnel.upsell].forEach((product) => {
        if (product?.id) selectedProductIds.add(product.id);
      });
    });

    return Array.from(selectedProductIds).map((productId) => {
      const product = selectedFunnels
        .flatMap((funnel) => [funnel.mainProduct, funnel.orderBump, funnel.upsell])
        .find((item) => item?.id === productId);
      const options = websites
        .filter((website) => website.products?.some((item) => item.product_id === productId))
        .map((website) => ({
          websiteId: website.websiteId,
          websiteName: website.name,
          createdAt: website.createdAt,
        }))
        .sort((a, b) => {
          const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
          const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
          return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
        });

      return {
        productId,
        productName: product?.name || "Producto",
        productImage: product?.image,
        options,
      };
    }).filter((item) => item.options.length > 0);
  }, [selectedFunnels, websites]);

  useEffect(() => {
    selectedProductLandingOptions.forEach((product) => {
      if (!data.productLandingLinks[product.productId] && product.options[0]?.websiteId) {
        setProductLandingLink(product.productId, product.options[0].websiteId);
      }
    });
  }, [selectedProductLandingOptions, data.productLandingLinks, setProductLandingLink]);

  const handleFunnelSelect = async (funnel: FunnelData) => {
    const website = websites.find((item) => item.websiteId === funnel.id);
    const summary = websiteSummaries.find((item) => item.id === funnel.id);

    if (!website) return;

    setLoading(true);
    try {
      const { funnelResponse, brandContextResponse } = await buildSelectedFunnel(website, summary);
      setFunnelBrandContextMap((current) => {
        const next = new Map(current);
        next.set(funnelResponse.id, brandContextResponse);
        return next;
      });
      addFunnel(funnelResponse);
      setSearchTerm('');
      setIsDropdownOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full min-h-[560px] flex-col">
      <div className="mb-6 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-12 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-fluxi focus:ring-4 focus:ring-blue-100"
            placeholder={dictionary["search-funnels"]?.[language as Language] ?? "Buscar y seleccionar embudos..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            onClick={() => setIsDropdownOpen(true)}
          />
          <ChevronDown
            className={`absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''
              }`}
          />

          {isDropdownOpen && (
            <div className="absolute left-0 right-0 top-full z-10 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-xl shadow-slate-200/60">
              {selectorLoading ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  Cargando embudos...
                </div>
              ) : websitesLoadError ? (
                <div className="p-4 text-center text-sm text-red-500">
                  {websitesLoadError}
                </div>
              ) : availableFunnels.length > 0 ? (
                availableFunnels.map((funnel) => (
                  <button
                    key={funnel.id}
                    type="button"
                    className="flex w-full items-center gap-3 rounded-xl p-3 text-left transition hover:bg-slate-50"
                    onClick={() => handleFunnelSelect(funnel)}
                  >
                    <img
                      src={funnel.mainProduct.image}
                      alt={funnel.mainProduct.name}
                      className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <h5 className="truncate font-semibold text-slate-900">
                        <span className="mr-2 text-slate-400">#{funnel.number}</span>
                        {funnel.name}
                      </h5>
                      <p className="truncate text-sm text-slate-500">
                        {funnel.mainProduct.name}
                      </p>
                      <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                        <div className="flex items-center">
                          <Package className="w-3 h-3 mr-1" />
                          {funnel.totalProducts} productos
                        </div>
                        <span>${funnel.mainProduct.price}</span>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-sm text-slate-500">
                  {searchTerm ? dictionary["no-funnels-found"]?.[language as Language] ?? "No se encontraron embudos" : dictionary["no-funnels-available"]?.[language as Language] ?? "No hay más embudos disponibles"}
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <div className="scrollable-container h-full overflow-y-auto pr-1">
          {selectedFunnels.length > 0 ? (
            <div className="space-y-4">
              {selectedFunnels.map((funnel) => (
                <div key={funnel.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="mb-4 flex items-center justify-between">
                    <h5 className="truncate pr-2 font-semibold text-slate-950">
                      <span className="mr-2 text-slate-400">#{funnel.number}</span>
                      {funnel.name}
                    </h5>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeFunnel(funnel.id)}
                        className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="flex min-w-0 items-center gap-3 rounded-xl bg-slate-50 p-3">
                      <img
                        src={funnel.mainProduct.image}
                        alt={funnel.mainProduct.name}
                        className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-400">Producto principal</p>
                        <p className="truncate text-sm font-semibold text-slate-900">{funnel.mainProduct.name}</p>
                        <p className="text-sm text-slate-500">${funnel.mainProduct.price}</p>
                      </div>
                    </div>

                    {funnel.orderBump && (
                      <div className="flex min-w-0 items-center gap-3 rounded-xl bg-slate-50 p-3">
                        <img
                          src={funnel.orderBump.image}
                          alt={funnel.orderBump.name}
                          className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-400">Order bump</p>
                          <p className="truncate text-sm font-semibold text-slate-900">{funnel.orderBump.name}</p>
                          <p className="text-sm text-slate-500">${funnel.orderBump.price}</p>
                        </div>
                      </div>
                    )}

                    {funnel.upsell && (
                      <div className="flex min-w-0 items-center gap-3 rounded-xl bg-slate-50 p-3">
                        <img
                          src={funnel.upsell.image}
                          alt={funnel.upsell.name}
                          className="h-12 w-12 flex-shrink-0 rounded-xl object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-400">Upsell</p>
                          <p className="truncate text-sm font-semibold text-slate-900">{funnel.upsell.name}</p>
                          <p className="text-sm text-slate-500">${funnel.upsell.price}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {selectedProductLandingOptions.length > 0 && (
                <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-fluxi shadow-sm">
                      <Link2 className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-950">Productos enlazados</h4>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                    Fluxi usa por defecto la landing más reciente de cada producto.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {selectedProductLandingOptions.map((product) => (
                      <div key={product.productId} className="grid gap-2 rounded-xl bg-white p-3 shadow-sm ring-1 ring-blue-100">
                        <div className="flex items-center gap-2">
                          {product.productImage && (
                            <img src={product.productImage} alt={product.productName} className="h-9 w-9 rounded-md object-cover" />
                          )}
                          <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{product.productName}</span>
                        </div>
                        <select
                          className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-fluxi focus:ring-4 focus:ring-blue-100"
                          value={data.productLandingLinks[product.productId] || product.options[0]?.websiteId || ""}
                          onChange={(event) => setProductLandingLink(product.productId, event.target.value)}
                        >
                          {product.options.map((option, index) => (
                            <option key={option.websiteId} value={option.websiteId}>
                              {option.websiteName}{index === 0 ? " (más reciente)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
                <Package className="h-8 w-8 text-fluxi" />
              </div>
              <p className="max-w-sm text-sm leading-6 text-slate-500">
                {dictionary["funnels-selected-home-label"]?.[language as Language] ?? "Acá aparecerán los embudos que selecciones"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
